import { $window } from '@umu/umu-dom';
import { fetch, fetchData } from '@umu/umu-fetch';
import { FILE_PROCESS_API_OPTION } from './api';
import { getDownloadUrl } from '../../../@utils';
import AsyncDownloadFileDialog from '../components/asyncDownloadFileDialog/asyncDownloadFileDialog';
import {
    BinaryUploadResponse,
    DownloadData,
    DownloadDataRequestParams,
    DownloadExcelFileResponse,
    IProcessFileDownloadOptions,
    IProgressRes,
    IUpLoadFileRes,
    UploadDataRequest,
} from '../interface';

export const idKeyList = ['instance_id', 'assessment_id'];
export const urlKeyList = [' file_url', 'excel_url'];

export function adaptiveValue(object: { [key: string]: string }, list: string[], value: any) {
    if (!value) {
        return;
    }
    const key = Object.keys(object).find((item) => list.includes(item));
    if (key) {
        const keyValue = object[key] || key;
        object[keyValue] = value;
    }
}

export function adaptiveId(object: { [key: string]: string }, id?: string) {
    adaptiveValue(object, idKeyList, id);
}

export function adaptiveUrl(object: { [key: string]: string }, url?: string) {
    adaptiveValue(object, urlKeyList, url);
}

export function adaptiveParamsJSON(params: DownloadDataRequestParams, id?: string) {
    if (!params.params) {
        return JSON.stringify({});
    }
    adaptiveId(params.params, id);
    return JSON.stringify(params.params);
}

export function adaptiveUploadOptions(options: UploadDataRequest, url?: string, id?: string) {
    const optionsNew = { ...options };
    if (!options.params) {
        return options;
    }
    const paramsNew = { ...options.params };
    adaptiveId(paramsNew as { [key: string]: string }, id);
    adaptiveUrl(paramsNew as { [key: string]: string }, url);
    optionsNew.params = paramsNew;
    return optionsNew;
}

export async function generalRequest(options: UploadDataRequest, id?: string) {
    const { api, params } = options as Required<UploadDataRequest>;
    if (id) {
        adaptiveId(params as { [key: string]: string }, id);
    }
    return fetchData(api, params, { silent: true }).then((res: any) => {
        return res;
    });
}

export const processFileUploadAndProgress = (options: IProcessFileDownloadOptions) => {
    const { params, apiType } = options;
    return fetchData(FILE_PROCESS_API_OPTION[apiType], params, { silent: true }).then(
        (res: { data: IProgressRes | IUpLoadFileRes }) => {
            return res;
        },
    );
};

export async function binaryUpload(options: UploadDataRequest, file: File): Promise<BinaryUploadResponse> {
    const {
        api,
        params: { excel = '' },
    } = options as Required<UploadDataRequest>;

    const formData = new FormData();
    formData.append(excel, file);

    const optionsNew = {
        body: formData,
        method: api.type,
    };

    return fetch.request(api.url, { ...optionsNew }).then((res: BinaryUploadResponse) => {
        return res;
    });
}

export const objectUpload = (options: UploadDataRequest, url?: string, id?: string) => {
    const optionsNew = adaptiveUploadOptions(options, url, id);
    return generalRequest(optionsNew);
};

export const objectUploadProgress = (options: UploadDataRequest, instance_id?: string) => {
    adaptiveId(options.params as { [key: string]: string }, instance_id);
    return generalRequest(options);
};

export const downloadExcelFileOfClass = (downloadData: DownloadData, id?: string) => {
    const { api, name, params } = downloadData as Required<DownloadData>;
    adaptiveId(params.params as { [key: string]: string }, id);
    return fetchData(
        api,
        {
            class: params.class,
            params: JSON.stringify(params.params),
        },
        { silent: true },
    ).then((res: DownloadExcelFileResponse) => {
        const { file_url = '', is_async, status, download_url } = res.data;
        const url = file_url || download_url;

        if (is_async || Number(status) === 1) {
            return AsyncDownloadFileDialog();
        }

        if (url && url != '') {
            const urlNew = getDownloadUrl(url, name || '', 'xlsx');
            $window.open(urlNew);
        }
        return;
    });
};

export const downloadExcelFileOfType = (downloadData: DownloadData, id?: string) => {
    const { api, name, params } = downloadData as Required<DownloadData>;
    adaptiveId(params as { [key: string]: string }, id);
    return fetchData(api, params, { silent: true }).then((res: DownloadExcelFileResponse) => {
        const { file_url = '', is_async, download_url } = res.data;
        const url = file_url || download_url;

        if (is_async) {
            return AsyncDownloadFileDialog();
        }
        if (url && url != '') {
            const urlNew = getDownloadUrl(url, name || '', 'xlsx');
            $window.open(urlNew);
        }
        return;
    });
};

export const generateUrl = (url: string, params: DownloadDataRequestParams, id?: string) => {
    const paramsNew = { ...params };
    paramsNew.params = adaptiveParamsJSON(params, id);
    const querString = Object.keys(paramsNew).reduce((prev, curr) => {
        return (prev += `&${curr}=${paramsNew[curr as keyof typeof params]}`);
    }, '');
    if (querString.length) {
        return url + '?' + querString.slice(1);
    }
    return url;
};

export const downloadExcelFile = (downloadData: DownloadData, id?: string) => {
    const { url, params } = downloadData;
    if (url && params) {
        const urlNew = generateUrl(url, params, id);
        $window.open(urlNew);
    } else if (url) {
        $window.open(url);
    } else if (params) {
        if (params.class) {
            downloadExcelFileOfClass(downloadData, id);
        } else if (params.type) {
            downloadExcelFileOfType(downloadData, id);
        }
    }
};
